import type { Rgba } from "./colors"
import {
	adaptTextColor,
	formatCssColor,
	isNeutralColor,
	parseCssColor,
	relativeLuminance,
} from "./colors"

/**
 * Layout and typography properties an email may keep. Everything outside this
 * set is dropped, so senders cannot reposition or cover the surrounding UI.
 */
const ALLOWED_PROPERTIES = new Set([
	"background-color",
	"border",
	"border-bottom",
	"border-bottom-color",
	"border-bottom-left-radius",
	"border-bottom-right-radius",
	"border-bottom-style",
	"border-bottom-width",
	"border-collapse",
	"border-color",
	"border-left",
	"border-left-color",
	"border-left-style",
	"border-left-width",
	"border-radius",
	"border-right",
	"border-right-color",
	"border-right-style",
	"border-right-width",
	"border-spacing",
	"border-style",
	"border-top",
	"border-top-color",
	"border-top-left-radius",
	"border-top-right-radius",
	"border-top-style",
	"border-top-width",
	"border-width",
	"box-sizing",
	"caption-side",
	"clear",
	"color",
	"direction",
	"display",
	"empty-cells",
	"float",
	"font-size",
	"font-style",
	"font-variant",
	"font-weight",
	"height",
	"letter-spacing",
	"line-height",
	"list-style",
	"list-style-position",
	"list-style-type",
	"margin",
	"margin-bottom",
	"margin-left",
	"margin-right",
	"margin-top",
	"max-height",
	"max-width",
	"min-height",
	"min-width",
	"opacity",
	"overflow-wrap",
	"padding",
	"padding-bottom",
	"padding-left",
	"padding-right",
	"padding-top",
	"table-layout",
	"text-align",
	"text-decoration",
	"text-indent",
	"text-transform",
	"vertical-align",
	"visibility",
	"white-space",
	"width",
	"word-break",
	"word-spacing",
	"word-wrap",
])

const COLOR_PROPERTIES = new Set(["color"])

const BACKGROUND_PROPERTIES = new Set(["background", "background-color"])

const BORDER_SHORTHANDS = new Set([
	"border",
	"border-bottom",
	"border-left",
	"border-right",
	"border-top",
])

const BORDER_COLOR_PROPERTIES = new Set([
	"border-bottom-color",
	"border-color",
	"border-left-color",
	"border-right-color",
	"border-top-color",
])

const UNSAFE_VALUE = /url\(|expression\(|javascript:|@import|<|\\/i

/** Backgrounds this light or this dark carry no intent worth keeping. */
const BACKGROUND_LIGHT_LUMINANCE = 0.86
const BACKGROUND_DARK_LUMINANCE = 0.06

const isDroppableBackground = (color: Rgba): boolean => {
	if (color.a < 0.1) return true
	if (!isNeutralColor(color)) return false
	const luminance = relativeLuminance(color)
	return (
		luminance >= BACKGROUND_LIGHT_LUMINANCE ||
		luminance <= BACKGROUND_DARK_LUMINANCE
	)
}

const BORDER_TOKEN_COLOR = "var(--email-border)"

const rewriteBorderShorthand = (value: string): string =>
	value
		.split(/\s+/)
		.map((token) => {
			const color = parseCssColor(token)
			if (!color) return token
			return isNeutralColor(color) ? BORDER_TOKEN_COLOR : token
		})
		.join(" ")

interface Declaration {
	property: string
	value: string
}

const parseDeclarations = (style: string): Declaration[] => {
	const declarations: Declaration[] = []
	for (const chunk of style.split(";")) {
		const separator = chunk.indexOf(":")
		if (separator === -1) continue
		const property = chunk.slice(0, separator).trim().toLowerCase()
		const value = chunk
			.slice(separator + 1)
			.replace(/!\s*important/gi, "")
			.trim()
		if (!property || !value) continue
		declarations.push({ property, value })
	}
	return declarations
}

/**
 * Keeps an email's layout intact while forcing text colors back onto the app's
 * own palette, so a message written for a white canvas stays readable in dark mode.
 */
export const filterEmailStyleAttribute = (
	style: string,
	isDark: boolean,
): string => {
	const declarations = parseDeclarations(style)

	const keepsOwnBackground = declarations.some(({ property, value }) => {
		if (!BACKGROUND_PROPERTIES.has(property)) return false
		const color = parseCssColor(value)
		return !!color && !isDroppableBackground(color)
	})

	const kept: string[] = []

	for (const { property, value } of declarations) {
		if (UNSAFE_VALUE.test(value)) continue

		if (BACKGROUND_PROPERTIES.has(property)) {
			const color = parseCssColor(value)
			if (!color || isDroppableBackground(color)) continue
			kept.push(`background-color: ${formatCssColor(color)}`)
			continue
		}

		if (!ALLOWED_PROPERTIES.has(property)) continue

		if (COLOR_PROPERTIES.has(property)) {
			const color = parseCssColor(value)
			if (!color) continue
			// Text on the sender's own colored surface has to keep its own contrast.
			if (keepsOwnBackground) {
				kept.push(`color: ${formatCssColor(color)}`)
				continue
			}
			if (isNeutralColor(color)) continue
			kept.push(`color: ${formatCssColor(adaptTextColor(color, isDark))}`)
			continue
		}

		if (BORDER_COLOR_PROPERTIES.has(property)) {
			const color = parseCssColor(value)
			kept.push(
				`${property}: ${color && isNeutralColor(color) ? BORDER_TOKEN_COLOR : value}`,
			)
			continue
		}

		if (BORDER_SHORTHANDS.has(property)) {
			kept.push(`${property}: ${rewriteBorderShorthand(value)}`)
			continue
		}

		kept.push(`${property}: ${value}`)
	}

	return kept.join("; ")
}

/** `bgcolor` / `<font color>` follow the same rules as their CSS counterparts. */
export const filterPresentationalColor = (
	value: string,
	kind: "background" | "text",
	isDark: boolean,
): string | null => {
	const color = parseCssColor(value)
	if (!color) return null
	if (kind === "background") {
		return isDroppableBackground(color) ? null : formatCssColor(color)
	}
	if (isNeutralColor(color)) return null
	return formatCssColor(adaptTextColor(color, isDark))
}
